// ============================================================
// PWA install plumbing — capture the browser's `beforeinstallprompt`
// event as early as possible so any UI (the footer "Install the app"
// button, the mobile banner) can trigger the native desktop/mobile
// install on demand. The event fires ONCE, early, and is consumable
// only once — so we stash it module-side and notify subscribers.
//
// "Installer l'app sur le bureau" = installing the site as a PWA :
// Chromium (Chrome / Edge / Brave) on desktop + Android opens it in a
// standalone window with its own desktop/Start-menu/dock icon. iOS
// Safari has no programmatic prompt → we surface a manual hint instead.
// ============================================================

let deferredPrompt = null;
const subscribers = new Set();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // keep the mini-infobar from auto-showing; we drive it
    deferredPrompt = e;
    subscribers.forEach((fn) => fn(true));
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    subscribers.forEach((fn) => fn(false));
  });
}

/** True if the browser has offered a native install we can trigger. */
export function canInstall() {
  return !!deferredPrompt;
}

/** Subscribe to install-availability changes. Returns an unsubscribe fn. */
export function subscribeInstall(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

/** Fire the native install prompt. Returns the user's choice outcome. */
export async function triggerInstall() {
  if (!deferredPrompt) return { outcome: 'unavailable' };
  deferredPrompt.prompt();
  let choice = { outcome: 'unknown' };
  try { choice = await deferredPrompt.userChoice; } catch { /* dismissed */ }
  if (choice?.outcome === 'accepted') {
    deferredPrompt = null;
    subscribers.forEach((fn) => fn(false));
  }
  return choice;
}

/** True if the site is already running as an installed standalone app. */
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia('(display-mode: standalone)').matches
      || window.matchMedia('(display-mode: minimal-ui)').matches
      || window.navigator.standalone === true; // iOS Safari
  } catch {
    return false;
  }
}

/** True on iOS Safari, where install is manual (Share → Add to Home Screen). */
export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent || '') && !window.MSStream;
}
